import { useCallback, useMemo } from 'react';

import { useUnit } from 'effector-react';

import { PAYMENT_OVERDUE_TODO } from '@m/constants/TodoTypes';
import { fetchVehicles, vehicleStore } from '@m/state/vehicle';
import { membershipStore } from 'apps/customer/state/membership';
import { fetchPaymentMethods, paymentStore } from 'apps/customer/state/payment';
import { routeStore } from 'apps/customer/state/route';
import { fetchTodoItems, todoStore } from 'apps/customer/state/todo';
import { setOnboardingTasks } from 'apps/customer/state/todo/store';
import { userStore } from 'apps/customer/state/user';
import { fetchCustomerVisits, visitStore } from 'apps/customer/state/visit';

import { contactInfoRequirementFor } from '../contactInfoRequirement';
import { deriveVariant } from '../deriveVariant';

import {
  resolveAfterAddEmail as resolveAfterAddEmailPure,
  resolveAfterAddPayment as resolveAfterAddPaymentPure,
  resolveAfterAddVehicle as resolveAfterAddVehiclePure,
  resolveAfterVerifyPhone as resolveAfterVerifyPhonePure,
  resolveResumedOnboarding,
  shouldShowAddVehiclePage,
} from './flowResolution';
import { onboardingSessionStore } from './onboardingSession';
import { isContactCollectedLast } from './stepOrder';

import type { OnboardingVariant } from '../deriveVariant';
import type { FlowNextAction } from './flowResolution';
import type { VerifyAndRegisterResult } from 'apps/customer/state/user/actions';

type VerifiedPhoneResult = Pick<VerifyAndRegisterResult, 'isNewAccount' | 'user'>;

export type OnboardingFlowResolution = {
  contactInfoRequirement: ReturnType<typeof contactInfoRequirementFor>;
  hasCompletedFlow: boolean;
  hasVerificationPhone: boolean;
  hasVerifiedSession: boolean;
  resolveAfterAddEmail: () => FlowNextAction;
  resolveAfterAddPayment: () => FlowNextAction;
  resolveAfterAddVehicle: (hasPaymentTask: boolean) => FlowNextAction;
  resolveAfterVerifyPhone: (result: VerifiedPhoneResult) => Promise<FlowNextAction>;
  resumeAction: FlowNextAction | null;
  variant: OnboardingVariant;
};

function responseError(error: string | undefined, fallback: string): Error {
  return new Error(error || fallback);
}

/** Resolves sign-in onboarding actions from current account state and freshly loaded API state. */
export function useOnboardingFlowResolution(): OnboardingFlowResolution {
  const { user, isInvitedUser, enterpriseUser, phoneNumber } = useUnit(userStore);
  const { individualSubscriptionInvitationId } = useUnit(membershipStore);
  const { isFlowComplete, isReturningUser, pendingSubscriptionResume } =
    useUnit(onboardingSessionStore);
  const { hasFetchedTodo, hasPaymentTask, hasRequiredTasks, onboardingTasks } = useUnit(todoStore);
  const { vehicles } = useUnit(vehicleStore);
  const { defaultPayment } = useUnit(paymentStore);
  const { visits, hasActiveVisit } = useUnit(visitStore);
  const { signInLandingPage, promptUserSignIn } = useUnit(routeStore);

  const detectedVariant = deriveVariant({
    enterpriseUser,
    individualSubscriptionInvitationId,
    siteId: null,
  });
  const variant =
    detectedVariant === 'agnostic'
      ? (pendingSubscriptionResume?.variant ?? detectedVariant)
      : detectedVariant;
  const contactCollectedLast = isContactCollectedLast({
    variant,
    isNewAccount: isReturningUser === false,
  });
  const currentEmail = user?.email;
  const hasOpenActiveVisit = hasActiveVisit === true;
  const vehicleCount = vehicles?.length ?? 0;
  const resumeAction = useMemo(() => {
    if (isReturningUser === null) return null;
    if (
      isReturningUser &&
      !hasFetchedTodo &&
      variant !== 'enterpriseSub' &&
      variant !== 'individualSub'
    ) {
      return null;
    }

    return resolveResumedOnboarding({
      email: currentEmail,
      hasActiveVisit: hasOpenActiveVisit,
      hasDefaultPayment: defaultPayment != null,
      hasName: !!(user?.firstName && user.lastName),
      hasPaymentTask: hasPaymentTask === true,
      hasRequiredTasks: hasRequiredTasks === true,
      hasVehicle: vehicleCount > 0,
      isNewAccount: !isReturningUser,
      missedEntryStartVisitOnly:
        !!onboardingTasks?.missedEntryStartVisit && onboardingTasks.list.length === 1,
      promptUserSignIn: !!promptUserSignIn,
      signInLandingPage,
      variant,
    });
  }, [
    currentEmail,
    defaultPayment,
    hasFetchedTodo,
    hasOpenActiveVisit,
    hasPaymentTask,
    hasRequiredTasks,
    isReturningUser,
    onboardingTasks,
    promptUserSignIn,
    signInLandingPage,
    user?.firstName,
    user?.lastName,
    variant,
    vehicleCount,
  ]);

  const resolveAfterVerifyPhone = useCallback(
    async ({ isNewAccount, user: verifiedUser }: VerifiedPhoneResult): Promise<FlowNextAction> => {
      if (isNewAccount) {
        return resolveAfterVerifyPhonePure({ isNewAccount: true, variant });
      }

      const [todoResponse, vehiclesResponse, visitsResponse] = await Promise.all([
        fetchTodoItems(),
        fetchVehicles(),
        fetchCustomerVisits(),
      ]);

      if (!todoResponse.success) {
        throw responseError(todoResponse.error, 'Unable to load onboarding tasks.');
      }
      if (!vehiclesResponse.success) {
        throw responseError(vehiclesResponse.error, 'Unable to load saved vehicles.');
      }
      if (!visitsResponse.success) {
        throw responseError(visitsResponse.error, 'Unable to load active visits.');
      }

      const todoItems = todoResponse.data?.todoItems ?? [];
      const tasks = setOnboardingTasks(todoItems);
      const freshVehicles = vehiclesResponse.data ?? [];

      let hasDefaultPayment = false;
      if (verifiedUser.id != null) {
        const paymentResponse = await fetchPaymentMethods({ userId: verifiedUser.id });
        if (!paymentResponse.success) {
          throw responseError(paymentResponse.error, 'Unable to load payment methods.');
        }
        hasDefaultPayment = (paymentResponse.data?.paymentMethods?.length ?? 0) > 0;
      }

      return resolveAfterVerifyPhonePure({
        isNewAccount: false,
        variant,
        hasPaymentTask: todoItems.some((todo) => todo.type === PAYMENT_OVERDUE_TODO),
        hasRequiredTasks: todoItems.some((todo) => todo.required),
        missedEntryStartVisitOnly: !!tasks.missedEntryStartVisit && tasks.list.length === 1,
        email: verifiedUser.email,
        hasName: !!(verifiedUser.firstName && verifiedUser.lastName),
        hasVehicle: freshVehicles.length > 0,
        promptUserSignIn: !!promptUserSignIn,
        hasDefaultPayment,
        signInLandingPage,
        hasActiveVisit: visitStore.getState().hasActiveVisit === true,
      });
    },
    [promptUserSignIn, signInLandingPage, variant],
  );

  const resolveAfterAddVehicle = useCallback(
    (hasPaymentTask: boolean): FlowNextAction =>
      resolveAfterAddVehiclePure({
        variant,
        hasPaymentTask,
        hasDefaultPayment: defaultPayment != null,
        signInLandingPage,
        isVisitOpen: hasOpenActiveVisit,
      }),
    [defaultPayment, hasOpenActiveVisit, signInLandingPage, variant],
  );

  const resolveAfterAddPayment = useCallback(
    (): FlowNextAction =>
      resolveAfterAddPaymentPure({
        contactCollectedLast,
        email: currentEmail,
        variant,
      }),
    [contactCollectedLast, currentEmail, variant],
  );

  const resolveAfterAddEmail = useCallback((): FlowNextAction => {
    const openVisit = visits[0];
    return resolveAfterAddEmailPure({
      contactCollectedLast,
      variant,
      isInvitedUser: !!isInvitedUser,
      hasRequiredTasks: hasRequiredTasks === true,
      hasVehicle: vehicleCount > 0,
      hasDefaultPayment: defaultPayment !== null,
      showAddVehiclePage: shouldShowAddVehiclePage({
        visitOpen: !!openVisit?.open,
        openVisitVehicleId: openVisit?.vehicle?.id,
        ownedVehicleIds: (vehicles ?? []).map((vehicle) => vehicle.id),
        sharedVehicleRoleByVehicleId: Object.fromEntries(
          (vehicles ?? []).map((vehicle) => [vehicle.id, vehicle.sharedVehicleRole?.name]),
        ),
      }),
    });
  }, [
    contactCollectedLast,
    defaultPayment,
    hasRequiredTasks,
    isInvitedUser,
    variant,
    vehicleCount,
    vehicles,
    visits,
  ]);

  return {
    variant,
    contactInfoRequirement: contactInfoRequirementFor(variant),
    hasCompletedFlow: isFlowComplete,
    hasVerifiedSession: isReturningUser !== null,
    hasVerificationPhone: !!phoneNumber,
    resumeAction,
    resolveAfterVerifyPhone,
    resolveAfterAddVehicle,
    resolveAfterAddPayment,
    resolveAfterAddEmail,
  };
}
